import { apiClient } from './client';

/**
 * 대시보드 기본판 API (migration 173 · 시안 화면 I).
 *
 * backend: apps/chat-server/src/modules/dashboard-template/dashboard-template.routes.ts
 *
 * ★★★ **개인 배치는 여기 없다.** 사용자의 보드는 여전히 브라우저에 산다(`store/dashboardStore`).
 *     이 API 가 주는 것은 회사가 정한 **기본판**뿐이다 — 전사 보드 · 신규 사용자 첫 배치 ·
 *     부서별 첫 배치.
 *
 * ★★★ 「이미 쓰는 사람에게 덮어쓰기」는 **스탬프**다(`forcedRevision`). 서버가 남의 브라우저를
 *     직접 못 고치므로, 각자 **다음에 열 때** 제 배치를 갈아 끼운다.
 */

export type TemplateKind = 'CORP' | 'NEW_USER' | 'ORG';
export type LoginLanding = 'CORP' | 'PERSONAL' | 'LAST';
/** FIRST(맨 앞 고정) | FREE(사용자가 옮길 수 있음) — migration 175. */
export type TabPosition = 'FIRST' | 'FREE';
export type TemplateSpan = 4 | 6 | 8 | 12;

export interface TemplateWidget { key: string; span: TemplateSpan }

export interface DashboardTemplateDto {
  id: string;
  kind: TemplateKind;
  orgId: string | null;
  name: string;
  widgets: TemplateWidget[];
  enabled: boolean;
  loginLanding: LoginLanding;
  /** 사용자가 이 탭을 ⋯ 메뉴에서 숨길 수 있는가 (migration 175). */
  userCanHide: boolean;
  tabPosition: TabPosition;
  revision: number;
  forcedRevision: number;
  updatedAt: string;
}

/** 사용자 — 내가 받을 기본판. 꺼진 것은 **서버가 이미 빼고** 준다. */
export async function listMyTemplates(): Promise<DashboardTemplateDto[]> {
  const { data } = await apiClient.get('/dashboard-templates');
  return data.templates ?? [];
}

/** 관리자 — 셋 다. 아직 안 정한 종류는 **목록에 없다**(「없다」와 「비워 두기로 정했다」는 다르다). */
export async function listAdminTemplates(): Promise<DashboardTemplateDto[]> {
  const { data } = await apiClient.get('/admin/dashboard-templates');
  return data.templates ?? [];
}

export interface TemplateWriteInput {
  name?: string;
  orgId?: string | null;
  widgets?: TemplateWidget[];
  enabled?: boolean;
  loginLanding?: LoginLanding;
  userCanHide?: boolean;
  tabPosition?: TabPosition;
}

export async function saveTemplate(kind: TemplateKind, input: TemplateWriteInput): Promise<DashboardTemplateDto> {
  const { data } = await apiClient.put(`/admin/dashboard-templates/${kind}`, input);
  return data.template;
}

/**
 * 이미 쓰고 있는 사람에게 덮어쓰기.
 *
 * ⚠ **되돌릴 수 없다.** 그리고 «지금 당장» 도 아니다 — 각자 다음에 열 때 갈아 끼워진다.
 *
 * ⛔ **2026-09-09 부터 화면에서 부르지 않는다.** 회사가 개인 대시보드를 채우고 덮어쓰던 길을
 *    걷어냈고(사용자 지시), 전사 탭은 잠긴 채 열 때마다 다시 그려져 덮어쓸 것이 없다.
 *    서버 라우트는 그대로 두었다 — 옛 화면이 남아 있는 배포를 깨뜨리지 않으려는 것뿐이고,
 *    **새로 부르지 말 것.**
 */
export async function forceTemplate(kind: TemplateKind): Promise<DashboardTemplateDto> {
  const { data } = await apiClient.post(`/admin/dashboard-templates/${kind}/force`, {});
  return data.template;
}
